// Pure text transforms behind the notes editor's Markdown toolbar. Each
// toolbar button toggles: applying a format that is already there removes it.

export interface TextEdit {
  value: string;
  selStart: number;
  selEnd: number;
}

// Block-level prefixes a line can carry. Order matters: the task prefix must
// be tried before the plain bullet, which it starts with.
const LINE_PREFIX_RE = /^(#{1,6} |[-*+] \[[ xX]\] |[-*+] |\d+[.)] |> )/;

const prefixKind = (prefix: string): string => {
  if (/^#{1,6} $/.test(prefix)) return prefix; // H2 and H3 are distinct formats
  if (/^[-*+] \[[ xX]\] $/.test(prefix)) return 'task';
  if (/^[-*+] $/.test(prefix)) return 'bullet';
  if (/^\d+[.)] $/.test(prefix)) return 'ordered';
  return prefix;
};

// Splits a line into its indentation, its block prefix (if any) and the rest.
const splitLine = (line: string) => {
  const indent = line.match(/^\s*/)![0];
  const afterIndent = line.slice(indent.length);
  const prefix = afterIndent.match(LINE_PREFIX_RE)?.[0] ?? '';
  return { indent, prefix, rest: afterIndent.slice(prefix.length) };
};

// Toggle a line prefix (heading, list, task, quote) on every selected line.
// If every selected line already has this kind of prefix it is removed;
// otherwise each line gets it, replacing any other block prefix it had, so
// clicking "task" on "- item" yields "- [ ] item" rather than "- [ ] - item".
export function toggleLinePrefix(value: string, selStart: number, selEnd: number, prefix: string): TextEdit {
  const lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
  const nextNewline = value.indexOf('\n', selEnd);
  const lineEnd = nextNewline === -1 ? value.length : nextNewline;
  const lines = value.slice(lineStart, lineEnd).split('\n');
  const multiLine = lines.length > 1;
  const kind = prefixKind(prefix);

  // Blank lines inside a multi-line selection are left alone.
  const skip = (line: string) => multiLine && line.trim() === '';
  const targets = lines.filter(l => !skip(l));
  const removing = targets.length > 0 && targets.every(l => {
    const { prefix: p } = splitLine(l);
    return p !== '' && prefixKind(p) === kind;
  });

  const newLines = lines.map(line => {
    if (skip(line)) return line;
    const { indent, rest } = splitLine(line);
    return removing ? indent + rest : indent + prefix + rest;
  });
  const newBlock = newLines.join('\n');
  const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);

  if (selStart === selEnd && !multiLine) {
    // Plain cursor: keep it on the same text, shifted by the prefix change.
    const delta = newBlock.length - lines[0].length;
    const cursor = Math.max(lineStart, Math.min(selStart + delta, lineStart + newBlock.length));
    return { value: newValue, selStart: cursor, selEnd: cursor };
  }
  return { value: newValue, selStart: lineStart, selEnd: lineStart + newBlock.length };
}

// Length of the run of `ch` ending at `pos` (going left) or starting at it.
const runLeft = (s: string, pos: number, ch: string) => {
  let n = 0;
  while (pos - n - 1 >= 0 && s[pos - n - 1] === ch) n++;
  return n;
};
const runRight = (s: string, pos: number, ch: string) => {
  let n = 0;
  while (pos + n < s.length && s[pos + n] === ch) n++;
  return n;
};

// Whether runs of the marker character on both sides mean the format is on.
// '*' and '**' share a character: a run of 1 or 3 means italic, 2 or 3 bold.
const markerActive = (marker: string, left: number, right: number) => {
  if (marker === '*') return (left === 1 || left === 3) && (right === 1 || right === 3);
  return left >= marker.length && right >= marker.length;
};

type MarkerState = 'outside' | 'inside' | null;

// Where the marker already wraps the range: right outside it (**|text|**),
// at its very edges (|**text**|), or nowhere.
const markerState = (value: string, start: number, end: number, marker: string): MarkerState => {
  const ch = marker[0];
  if (markerActive(marker, runLeft(value, start, ch), runRight(value, end, ch))) return 'outside';
  const selected = value.slice(start, end);
  if (selected.length >= 2 * marker.length
      && markerActive(marker, runRight(selected, 0, ch), runLeft(selected, selected.length, ch))
      && runRight(selected, 0, ch) < selected.length) return 'inside';
  return null;
};

const applyMarker = (value: string, start: number, end: number, marker: string, state: MarkerState): TextEdit => {
  const len = marker.length;
  const selected = value.slice(start, end);
  if (state === 'outside') {
    return { value: value.slice(0, start - len) + selected + value.slice(end + len), selStart: start - len, selEnd: end - len };
  }
  if (state === 'inside') {
    const inner = selected.slice(len, selected.length - len);
    return { value: value.slice(0, start) + inner + value.slice(end), selStart: start, selEnd: start + inner.length };
  }
  return { value: value.slice(0, start) + marker + selected + marker + value.slice(end), selStart: start + len, selEnd: end + len };
};

// The parts of the selection an inline marker may wrap: per line, the text
// after the block prefix ("- [ ] ", "## ", …) with surrounding spaces trimmed.
// Wrapping the prefix too ("**- [ ] task**") would break the list item.
const inlineSegments = (value: string, selStart: number, selEnd: number) => {
  const segments: { start: number; end: number }[] = [];
  let lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
  while (lineStart <= selEnd) {
    const nl = value.indexOf('\n', lineStart);
    const lineEnd = nl === -1 ? value.length : nl;
    const { indent, prefix } = splitLine(value.slice(lineStart, lineEnd));
    const contentStart = lineStart + indent.length + prefix.length;
    let start = Math.max(selStart, contentStart);
    let end = Math.min(selEnd, lineEnd);
    while (start < end && /\s/.test(value[start])) start++;
    while (end > start && /\s/.test(value[end - 1])) end--;
    if (start < end) segments.push({ start, end });
    if (nl === -1) break;
    lineStart = nl + 1;
  }
  return segments;
};

// Toggle a symmetric inline marker (bold, italic, strikethrough, inline code)
// around the selection. Markers already wrapping it are removed instead of
// being added a second time. A selection spanning several lines is wrapped
// line by line, and block prefixes such as list or task markers are kept
// outside the markers.
export function toggleInlineMarker(value: string, selStart: number, selEnd: number, marker: string): TextEdit {
  let segments = inlineSegments(value, selStart, selEnd);

  if (segments.length === 0) {
    // Plain cursor (or a selection of only a prefix/spaces): keep the cursor
    // out of the block prefix so the markers land in the text.
    const lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
    const nl = value.indexOf('\n', lineStart);
    const { indent, prefix } = splitLine(value.slice(lineStart, nl === -1 ? value.length : nl));
    const pos = Math.max(selStart, lineStart + indent.length + prefix.length);
    return applyMarker(value, pos, pos, marker, markerState(value, pos, pos, marker));
  }

  if (segments.length === 1) {
    const { start, end } = segments[0];
    return applyMarker(value, start, end, marker, markerState(value, start, end, marker));
  }

  // Several lines: the selection edges may cut through markers ("a**" /
  // "**b"), so each line's part is widened over adjacent marker characters.
  // Remove only if every line is already wrapped; otherwise wrap the lines
  // that are not. Apply from the end so offsets stay valid.
  const ch = marker[0];
  segments = segments.map(({ start, end }) => ({
    start: start - runLeft(value, start, ch),
    end: end + runRight(value, end, ch),
  }));
  const states = segments.map(seg => markerState(value, seg.start, seg.end, marker));
  const removing = states.every(st => st !== null);
  let result = value;
  for (let i = segments.length - 1; i >= 0; i--) {
    if (!removing && states[i] !== null) continue;
    result = applyMarker(result, segments[i].start, segments[i].end, marker, removing ? states[i] : null).value;
  }
  // Select from the first line's text to the last line's text, markers included.
  const lineStart = value.lastIndexOf('\n', segments[0].start - 1) + 1;
  const last = segments[segments.length - 1];
  // Text after the last segment is untouched, except a closing marker that
  // sat right after it and has just been removed.
  const removedAfter = removing && states[segments.length - 1] === 'outside' ? marker.length : 0;
  const tailLength = value.length - last.end - removedAfter;
  return { value: result, selStart: lineStart, selEnd: result.length - tailLength };
}

const DEFAULT_INDENT = 2;

// Indentation and list-marker width ("- " → 2, "1. " → 3) of a list line,
// or null when the line is not a list item.
const listInfo = (line: string) => {
  const m = line.match(/^(\s*)([-*+]|\d+[.)]) /);
  return m ? { indent: m[1].length, contentCol: m[0].length } : null;
};

const indentOf = (line: string) => line.match(/^\s*/)![0].length;

// Shift the selected lines one nesting level right (direction 1) or left
// (direction -1). A list item nests under the item above it, so the step is
// that parent's marker width — CommonMark needs 3 spaces under "1. ", not 2.
// All lines move by the same amount so their relative structure is kept.
export function shiftIndent(value: string, selStart: number, selEnd: number, direction: 1 | -1): TextEdit {
  const lineStart = value.lastIndexOf('\n', selStart - 1) + 1;
  // A selection ending right after a newline does not include the next line.
  const effectiveEnd = selEnd > selStart && value[selEnd - 1] === '\n' ? selEnd - 1 : selEnd;
  const nextNewline = value.indexOf('\n', effectiveEnd);
  const lineEnd = nextNewline === -1 ? value.length : nextNewline;
  const lines = value.slice(lineStart, lineEnd).split('\n');

  const first = lines.find(l => l.trim() !== '') ?? lines[0];
  const current = indentOf(first);
  const above = value.slice(0, Math.max(0, lineStart - 1)).split('\n').reverse()
    .filter(l => l.trim() !== '');

  let target: number;
  if (direction === 1) {
    // Nest under the closest list item above at this level or shallower.
    const parent = above.map(listInfo).find(info => info && info.indent <= current);
    target = parent && parent.contentCol > current ? parent.contentCol : current + DEFAULT_INDENT;
  } else {
    // Step back one level, mirroring the indent step: the deepest level below
    // the current one that a list item above sits at or nests into. A plain
    // DEFAULT_INDENT step back counts too, unless it would land inside a
    // list marker (e.g. 1 space under "1. ") where it is no valid level.
    const items = above.map(listInfo).filter((info): info is NonNullable<typeof info> => info !== null);
    const levels = items.flatMap(info => [info.indent, info.contentCol]).filter(level => level < current);
    const stepBack = current - DEFAULT_INDENT;
    if (stepBack >= 0 && !items.some(info => info.indent < stepBack && stepBack < info.contentCol)) {
      levels.push(stepBack);
    }
    target = levels.length > 0 ? Math.max(...levels) : 0;
  }
  const delta = target - current;
  if (delta === 0) return { value, selStart, selEnd };

  const newLines = lines.map(line => {
    if (line.trim() === '') return line;
    if (delta > 0) return ' '.repeat(delta) + line;
    return line.slice(Math.min(-delta, indentOf(line)));
  });
  const newBlock = newLines.join('\n');
  const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);

  if (selStart === selEnd && lines.length === 1) {
    const shift = newBlock.length - lines[0].length;
    const cursor = Math.max(lineStart, selStart + shift);
    return { value: newValue, selStart: cursor, selEnd: cursor };
  }
  return { value: newValue, selStart: lineStart, selEnd: lineStart + newBlock.length };
}
